
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Header from './components/Header';
import DataCard from './components/DataCard';
import PriceChart from './components/PriceChart';
import Calculator from './components/Calculator';
import Explanation from './components/Explanation';
import Alerts from './components/Alerts';
import Quote from './components/Quote';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import AboutUsModal from './components/AboutUsModal';
import ContactUsModal from './components/ContactUsModal';
import { ChartDataPoint, TimeRange, CookieSettings, Alert, Currency, FearAndGreedReading } from './types';
import { QUOTES } from './constants';
import * as auth from './utils/auth';
import { supabase } from './utils/supabaseClient';
import FearAndGreedIndex from './components/FearAndGreedIndex';
import AdBanner from './components/AdBanner';
import * as analytics from './utils/analytics';
import LoadingScreen from './components/LoadingScreen';
import BitcoinGuide from './components/BitcoinGuide';

const App: React.FC = () => {
  const [btcPriceUSD, setBtcPriceUSD] = useState<number | null>(null);
  const [btcPriceEUR, setBtcPriceEUR] = useState<number | null>(null);
  const [circulatingSupply, setCirculatingSupply] = useState<number | null>(null);
  const [marketCapWeeklyChange, setMarketCapWeeklyChange] = useState<number | null>(null);
  const [marketCapHistory, setMarketCapHistory] = useState<{ time: number, value: number }[]>([]);
  
  const historicalDataCache = useRef<Partial<Record<TimeRange, ChartDataPoint[]>>>({});
  const [activeChartData, setActiveChartData] = useState<ChartDataPoint[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('1Y');
  const [isChartLoading, setIsChartLoading] = useState(true);

  // Global loading state for AdSense compliance
  const [loadingStates, setLoadingStates] = useState({
    initialDetails: true,
    chart: true,
    fearAndGreed: true,
  });
  const [fearAndGreedData, setFearAndGreedData] = useState<FearAndGreedReading | null>(null);
  const [fearAndGreedError, setFearAndGreedError] = useState<string | null>(null);
  const initialChartLoadDone = useRef(false);

  // AdSense Policy Compliance State
  const [isContentLoaded, setIsContentLoaded] = useState(false);

  // Auth State
  const [currentUser, setCurrentUser] = useState<auth.User | null>(null);
  const [authModal, setAuthModal] = useState<'closed' | 'login' | 'signup'>('closed');
  const [authError, setAuthError] = useState<string>('');
  const [pendingConfirmation, setPendingConfirmation] = useState<{email: string} | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Modal State
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false);
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);
  const [isContactUsOpen, setIsContactUsOpen] = useState(false);
  
  // Cookie State
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  
  // Client-side Alerts State
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const isAppLoading = loadingStates.initialDetails || loadingStates.chart || loadingStates.fearAndGreed;

  const marketCapUSD = useMemo(() => {
    if (btcPriceUSD === null || circulatingSupply === null) {
      return null;
    }
    return (btcPriceUSD * circulatingSupply) / 1_000_000_000;
  }, [btcPriceUSD, circulatingSupply]);

  useEffect(() => {
    analytics.initializeAnalytics();
    // Load alerts from localStorage on initial mount
    const savedAlerts = localStorage.getItem('btcPriceAlerts');
    if (savedAlerts) {
      setAlerts(JSON.parse(savedAlerts));
    }
  }, []);

  // Effect to determine if content is loaded enough to show ads
  useEffect(() => {
    // We consider content "loaded" when the main app loader is gone.
    if (!isAppLoading) {
      // Use a small timeout to ensure content has a moment to render before ads are injected,
      // preventing layout shifts and ensuring compliance.
      const timer = setTimeout(() => {
        setIsContentLoaded(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAppLoading]);
  
  // Save alerts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('btcPriceAlerts', JSON.stringify(alerts));
  }, [alerts]);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  useEffect(() => {
    // Supabase provides a real-time auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ? { username: session.user.user_metadata?.username || session.user.email } : null;
      setCurrentUser(user);
      setLoadingAuth(false);
      
      if (_event === 'SIGNED_IN') {
        setAuthModal('closed');
      }
    });

    // On initial load, check if there's already a session
    const checkSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user ? { username: session.user.user_metadata?.username || session.user.email } : null;
        setCurrentUser(user);
        setLoadingAuth(false);
    }
    checkSession();

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  const today = new Date();
  const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const dailyQuote = useMemo(() => QUOTES[dayOfYear % QUOTES.length], [dayOfYear]);

  // Fetch Fear & Greed Index
  useEffect(() => {
    const fetchFearAndGreedData = async () => {
      try {
        const response = await fetch('https://api.alternative.me/fng/?limit=1');
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }
        const result = await response.json();
        if (result && result.data && result.data.length > 0) {
          setFearAndGreedData(result.data[0]);
          setFearAndGreedError(null);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (e) {
        if (e instanceof Error) {
            setFearAndGreedError(e.message);
        } else {
            setFearAndGreedError('An unknown error occurred');
        }
        console.error("Failed to fetch Fear & Greed Index:", e);
      } finally {
        setLoadingStates(prev => ({ ...prev, fearAndGreed: false }));
      }
    };

    fetchFearAndGreedData();
  }, []);


  useEffect(() => {
    const timeRangeToKrakenParams: Record<TimeRange, { interval: number; points: number }> = {
      '1D': { interval: 60, points: 24 },    // Hourly for last 24 hours
      '1W': { interval: 60, points: 168 },   // Hourly for last 7 days (7*24)
      '3M': { interval: 240, points: 540 },  // 4-hourly for last 90 days (90*24/4)
      '6M': { interval: 1440, points: 183 }, // Daily for last 6 months
      '1Y': { interval: 1440, points: 365 }, // Daily for last year
    };

    const formatTimestamp = (timestamp: number, range: TimeRange): string => {
        const date = new Date(timestamp);
        switch (range) {
            case '1D':
                return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            case '1W':
            case '3M':
                 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
            default:
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    }

    const fetchHistoricalData = async (range: TimeRange) => {
      if (historicalDataCache.current[range]) {
        setActiveChartData(historicalDataCache.current[range]!);
        setIsChartLoading(false);
        return;
      }
      
      setIsChartLoading(true);
      try {
        const { interval, points } = timeRangeToKrakenParams[range];
        
        const [usdResponse, eurResponse] = await Promise.all([
          fetch(`https://api.kraken.com/0/public/OHLC?pair=XBTUSD&interval=${interval}`),
          fetch(`https://api.kraken.com/0/public/OHLC?pair=XBTEUR&interval=${interval}`)
        ]);

        if (!usdResponse.ok || !eurResponse.ok) {
          throw new Error('Failed to fetch historical data from Kraken for range: ' + range);
        }

        const usdData = await usdResponse.json();
        const eurData = await eurResponse.json();

        if (usdData.error?.length || eurData.error?.length) {
          const errorMessage = (usdData.error && usdData.error[0]) || (eurData.error && eurData.error[0]) || 'Unknown Kraken API error';
          throw new Error(`Kraken API error: ${errorMessage}`);
        }
        
        const usdPairName = Object.keys(usdData.result)[0];
        const eurPairName = Object.keys(eurData.result)[0];
        
        // Kraken returns [time, open, high, low, close, vwap, volume, count]
        // We slice to get the number of points needed for the selected time range.
        const usdPricesRaw: [number, string, string, string, string][] = usdData.result[usdPairName].slice(-points);
        const eurPricesRaw: [number, string, string, string, string][] = eurData.result[eurPairName].slice(-points);

        const eurPricesMap = new Map<number, number>(
            eurPricesRaw.map(([timestamp, _o, _h, _l, close]) => [timestamp, parseFloat(close)])
        );

        const formattedData: ChartDataPoint[] = usdPricesRaw.reduce((acc: ChartDataPoint[], [timestampSec, _o, _h, _l, closeUSD]) => {
          const priceEUR = eurPricesMap.get(timestampSec);
          const timestampMs = timestampSec * 1000;
          
          if (priceEUR !== undefined) {
            acc.push({
              timestamp: timestampMs,
              date: formatTimestamp(timestampMs, range),
              priceUSD: parseFloat(parseFloat(closeUSD).toFixed(2)),
              priceEUR: parseFloat(priceEUR.toFixed(2)),
            });
          }
          return acc;
        }, [] as ChartDataPoint[]);
        
        historicalDataCache.current[range] = formattedData;
        setActiveChartData(formattedData);

      } catch (error) {
        console.error(`Failed to fetch historical chart data for ${range}:`, error);
      } finally {
        setIsChartLoading(false);
        if (!initialChartLoadDone.current) {
          setLoadingStates(prev => ({ ...prev, chart: false }));
          initialChartLoadDone.current = true;
        }
      }
    };
    
    fetchHistoricalData(selectedTimeRange);
  }, [selectedTimeRange]);
  
  // Fetch initial data and then connect to WebSocket for live prices
  useEffect(() => {
    // 1. Fetch initial data from CoinGecko for market cap etc.
    const fetchInitialData = async () => {
      try {
        const [detailsResponse, chartResponse] = await Promise.all([
          fetch('https://api.coingecko.com/api/v3/coins/bitcoin'),
          fetch('https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=7&interval=daily')
        ]);

        if (!detailsResponse.ok) {
          throw new Error(`Coin details error: ${detailsResponse.status}`);
        }
        if (!chartResponse.ok) {
            throw new Error(`Market chart error: ${chartResponse.status}`);
        }
        
        const detailsData = await detailsResponse.json();
        const chartData = await chartResponse.json();
        
        if (detailsData && detailsData.market_data) {
          setBtcPriceUSD(detailsData.market_data.current_price.usd);
          setBtcPriceEUR(detailsData.market_data.current_price.eur);
          setCirculatingSupply(detailsData.market_data.circulating_supply);
          setMarketCapWeeklyChange(detailsData.market_data.price_change_percentage_7d);
        }
        
        if (chartData && chartData.market_caps) {
            const history: { time: number, value: number }[] = chartData.market_caps.map(
                ([time, value]: [number, number]) => ({ time, value })
            );
            setMarketCapHistory(history);
        }

      } catch (error) {
        console.error("Failed to fetch initial Bitcoin data:", error);
      } finally {
        setLoadingStates(prev => ({ ...prev, initialDetails: false }));
      }
    };

    fetchInitialData();

    // 2. Connect to Kraken WebSocket for real-time updates
    const ws = new WebSocket('wss://ws.kraken.com/');

    ws.onopen = () => {
      ws.send(JSON.stringify({
        event: 'subscribe',
        pair: ['XBT/USD', 'XBT/EUR'],
        subscription: { name: 'ticker' }
      }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // Check if it's a ticker update message
      if (Array.isArray(data) && data[2] === 'ticker') {
        const pair = data[3];
        const price = parseFloat(data[1].c[0]); // c[0] is the last trade price

        if (pair === 'XBT/USD') {
          setBtcPriceUSD(price);
        } else if (pair === 'XBT/EUR') {
          setBtcPriceEUR(price);
        }
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
    };

    // Cleanup on component unmount
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          event: 'unsubscribe',
          pair: ['XBT/USD', 'XBT/EUR'],
          subscription: { name: 'ticker' }
        }));
        ws.close();
      }
    };
  }, []);

  // Check for triggered alerts whenever the price or alerts list changes
  useEffect(() => {
    if (!currentUser || !btcPriceUSD || !btcPriceEUR || alerts.length === 0) return;
  
    const triggeredAlerts: string[] = [];
  
    alerts.forEach(alert => {
      const currentPrice = alert.currency === Currency.USD ? btcPriceUSD : btcPriceEUR;
      const conditionMet = 
        (alert.condition === 'above' && currentPrice > alert.targetPrice) ||
        (alert.condition === 'below' && currentPrice < alert.targetPrice);
  
      if (conditionMet) {
        const currencySymbol = alert.currency === Currency.USD ? '$' : '€';
        const title = `Bitcoin Price Alert!`;
        const body = `BTC has gone ${alert.condition} your target of ${currencySymbol}${alert.targetPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Current price: ${currencySymbol}${currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        
        new Notification(title, { body });
        triggeredAlerts.push(alert.id);
      }
    });
  
    if (triggeredAlerts.length > 0) {
      setAlerts(currentAlerts => currentAlerts.filter(a => !triggeredAlerts.includes(a.id)));
    }
  }, [btcPriceUSD, btcPriceEUR, alerts, currentUser]);


  const finalChartData = useMemo(() => {
    if (!activeChartData.length || btcPriceUSD === null || btcPriceEUR === null) {
      return activeChartData;
    }

    const intervalMillis: Record<TimeRange, number> = {
      '1D': 60 * 60 * 1000,       // 1 hour
      '1W': 60 * 60 * 1000,       // 1 hour
      '3M': 4 * 60 * 60 * 1000,   // 4 hours
      '6M': 24 * 60 * 60 * 1000,  // 1 day
      '1Y': 24 * 60 * 60 * 1000,  // 1 day
    };
    
    const currentInterval = intervalMillis[selectedTimeRange];
    const now = Date.now();
    // Calculate the start of the current interval bucket (e.g., top of the current hour)
    const boundaryTimestamp = Math.floor(now / currentInterval) * currentInterval;

    const lastPoint = activeChartData[activeChartData.length - 1];

    // Check if the last data point is from the current time interval
    if (lastPoint && lastPoint.timestamp >= boundaryTimestamp) {
      // If it is, update the last point with the latest real-time price
      return activeChartData.slice(0, -1).concat({
        ...lastPoint,
        priceUSD: btcPriceUSD,
        priceEUR: btcPriceEUR,
      });
    } else {
      // If the last data point is old, append a new point for the current price
      return [
        ...activeChartData,
        {
          timestamp: now,
          date: 'Now',
          priceUSD: btcPriceUSD,
          priceEUR: btcPriceEUR,
        },
      ];
    }
  }, [activeChartData, btcPriceUSD, btcPriceEUR, selectedTimeRange]);
  
  const finalMarketCapHistory = useMemo(() => {
    if (!marketCapHistory.length || marketCapUSD === null) {
      return marketCapHistory;
    }
    
    const currentMarketCapValue = marketCapUSD * 1_000_000_000;
    const now = Date.now();
    const lastPoint = marketCapHistory[marketCapHistory.length - 1];

    const todayStart = new Date();
    todayStart.setUTCHours(0, 0, 0, 0);
    const todayStartTimestamp = todayStart.getTime();

    // Check if the last data point from the API is for today
    if (lastPoint && lastPoint.time >= todayStartTimestamp) {
      // If it is, update the last point to the current time and value to reflect the live market cap
      const updatedHistory = [...marketCapHistory];
      updatedHistory[updatedHistory.length - 1] = { time: now, value: currentMarketCapValue };
      return updatedHistory;
    } else {
      // If the last point is from a previous day, append a new point for today's live value...
      const updatedHistory = [
        ...marketCapHistory,
        {
          time: now,
          value: currentMarketCapValue,
        },
      ];
      // ...and ensure we only show 8 data points (representing a 7-day trend).
      return updatedHistory.slice(-8);
    }
  }, [marketCapHistory, marketCapUSD]);

  const handleShowLogin = () => {
    setAuthError('');
    setPendingConfirmation(null);
    setAuthModal('login');
  };
  
  const handleLogout = async () => {
    await auth.logout();
    setCurrentUser(null);
  };
  
  const handleSignupSubmit = async (details: auth.SignupDetails) => {
    const result = await auth.signup(details);
    if (result.success) {
      setPendingConfirmation({ email: details.email });
      setAuthError('');
    } else {
      setAuthError(result.message);
    }
  };

  const handleLoginSubmit = async (details: auth.LoginDetails) => {
    const result = await auth.login(details);
    if (result.success) {
      // The onAuthStateChange listener will handle setting the user and closing the modal.
      setAuthError('');
    } else {
      setAuthError(result.message);
    }
  };
  
  const handleCloseAuthModal = () => {
    setAuthModal('closed');
    setPendingConfirmation(null);
  }

  // --- Cookie Management ---
  const handleSaveCookieSettings = (settings: CookieSettings) => {
    localStorage.setItem('cookieConsent', JSON.stringify(settings));
    analytics.updateConsent(settings.analytics, settings.marketing);
    setShowCookieBanner(false);
    setIsCookieSettingsOpen(false);
  };

  const handleAcceptAllCookies = () => {
    handleSaveCookieSettings({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectAllCookies = () => {
    handleSaveCookieSettings({ necessary: true, analytics: false, marketing: false });
  };
  
  // --- Modal Management ---
  const handleOpenCookieSettings = () => setIsCookieSettingsOpen(true);
  const handleOpenPrivacyPolicy = () => setIsPrivacyPolicyOpen(true);
  const handleOpenAboutUs = () => setIsAboutUsOpen(true);
  const handleOpenContactUs = () => setIsContactUsOpen(true);

  const handleSetAlert = (alert: Omit<Alert, 'id'>) => {
    const newAlert = { ...alert, id: new Date().toISOString() };
    setAlerts(prevAlerts => [...prevAlerts, newAlert]);
  };
  
  const handleRemoveAlert = (id: string) => {
    setAlerts(prevAlerts => prevAlerts.filter(a => a.id !== id));
  };
  
  const handleUpdateAlert = (updatedAlert: Alert) => {
    setAlerts(prevAlerts => prevAlerts.map(a => a.id === updatedAlert.id ? updatedAlert : a));
  };

  // Condition for showing ads, to comply with AdSense policies.
  const showAds = isContentLoaded && authModal === 'closed';

  if (isAppLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-dark-blue font-sans">
      <Header currentUser={currentUser} onLoginClick={handleShowLogin} onLogout={handleLogout} />
      
      {authModal !== 'closed' && (
        <AuthModal 
          mode={authModal} 
          onClose={handleCloseAuthModal}
          onSignup={handleSignupSubmit}
          onLogin={handleLoginSubmit}
          onSwitchMode={(newMode) => { setAuthError(''); setAuthModal(newMode); setPendingConfirmation(null); }}
          errorMessage={authError}
          pendingConfirmation={pendingConfirmation}
        />
      )}

      <main className="container mx-auto px-4 py-8">
        {confirmationMessage && (
            <div className="bg-green-900/50 border border-green-500 text-green-300 text-center p-3 rounded-lg mb-6">
                {confirmationMessage}
            </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <DataCard 
            title="BTC/USD Price" 
            value={btcPriceUSD !== null ? `$${btcPriceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Loading...'} 
            isRealtime={btcPriceUSD !== null} 
          />
          <DataCard 
            title="BTC/EUR Price" 
            value={btcPriceEUR !== null ? `€${btcPriceEUR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Loading...'} 
            isRealtime={btcPriceEUR !== null}
          />
          <DataCard 
            title="Market Cap (USD)" 
            value={marketCapUSD !== null ? `$${marketCapUSD.toFixed(2)}B` : 'Loading...'} 
            change={marketCapWeeklyChange ?? undefined}
            changePeriod="7d"
            trendData={finalMarketCapHistory}
          />
          <FearAndGreedIndex
            data={fearAndGreedData}
            loading={loadingStates.fearAndGreed}
            error={fearAndGreedError}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-8">
          <div className="lg:col-span-2 bg-navy-blue p-6 rounded-lg shadow-xl">
            <h2 className="text-2xl font-bold mb-4 text-off-white">Gain / Loss Calculator</h2>
            <Calculator 
              realtimePriceUSD={btcPriceUSD ?? 0}
              realtimePriceEUR={btcPriceEUR ?? 0}
            />
          </div>
          <div className="lg:col-span-3 bg-navy-blue p-6 rounded-lg shadow-xl">
            <PriceChart 
              data={finalChartData} 
              isLoading={isChartLoading} 
              selectedRange={selectedTimeRange}
              onRangeChange={setSelectedTimeRange}
            />
          </div>
        </div>

        <Explanation />
        <BitcoinGuide />
        
        {showAds && (
          <div className="my-8 text-center">
            <p className="text-xs text-light-blue mb-2">Advertisement</p>
            <div className="bg-dark-blue rounded-lg p-2 min-h-[100px]">
              <AdBanner
                data-ad-client="ca-pub-5297401074014823"
                data-ad-slot="YYYYYYYYYY"
                data-ad-format="auto"
                data-full-width-responsive="true"
              />
            </div>
          </div>
        )}

        {currentUser ? (
          <Alerts 
            activeAlerts={alerts}
            onSetAlert={handleSetAlert}
            onRemoveAlert={handleRemoveAlert}
            onUpdateAlert={handleUpdateAlert}
          />
        ) : (
          <div className="bg-navy-blue p-8 rounded-lg shadow-xl mb-8 text-center">
            <h2 className="text-2xl font-bold mb-4 text-off-white">Want Price Alerts?</h2>
            <p className="text-light-blue mb-6 max-w-lg mx-auto">
              Sign in or create an account to get browser notifications when Bitcoin hits your target price.
            </p>
            <button
              onClick={handleShowLogin}
              className="bg-btc-orange text-dark-blue font-bold py-3 px-6 rounded-lg hover:bg-orange-400 transition-colors duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-blue focus:ring-btc-orange"
            >
              Sign In to Set Alerts
            </button>
          </div>
        )}

        <Quote quote={dailyQuote.text} author={dailyQuote.author} />
      </main>

      <Footer 
        showAds={showAds}
        onPrivacyPolicyClick={handleOpenPrivacyPolicy}
        onAboutUsClick={handleOpenAboutUs}
        onContactUsClick={handleOpenContactUs}
      />
      
      <CookieSettingsModal 
        isOpen={isCookieSettingsOpen}
        onClose={() => setIsCookieSettingsOpen(false)}
        onSave={handleSaveCookieSettings}
        onAcceptAll={handleAcceptAllCookies}
      />
      
      <PrivacyPolicyModal 
        isOpen={isPrivacyPolicyOpen}
        onClose={() => setIsPrivacyPolicyOpen(false)}
      />

      <AboutUsModal
        isOpen={isAboutUsOpen}
        onClose={() => setIsAboutUsOpen(false)}
      />

      <ContactUsModal
        isOpen={isContactUsOpen}
        onClose={() => setIsContactUsOpen(false)}
      />

      {showCookieBanner && (
        <CookieBanner 
          onAccept={handleAcceptAllCookies}
          onReject={handleRejectAllCookies}
          onSettings={handleOpenCookieSettings}
          onPrivacyPolicyClick={handleOpenPrivacyPolicy}
        />
      )}
    </div>
  );
};

export default App;
